import {
  type RunnerStatsHistoryWire,
  RunnerStatsHistoryWireSchema,
  type RunnerStatsRange,
} from '@developerz/domain';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { FLEET_POLL_MS, fetchTasksPage, type TaskRow } from './fleet';
import { tasksPath } from './fleet-work';
import { createLiveQuery } from './live/live-query';
import type { Page } from './paged';

// The details panel's two per-machine reads (#6565): its stats history
// (`GET /v1/runners/:id/stats?range=`, #6558) and its newest work
// (`GET /v1/tasks?filter=runner_id:eq:<id>`, the read `/fleet/work?machine=` makes).
//
// THE MACHINE ID IS IN THE QUERY KEY, AND NOTHING KEEPS A PREVIOUS ANSWER. The panel
// switches machines without remounting (#6563), so a read started for machine A can land
// after B is selected. Keyed by id, A's answer fills A's cache entry and B's view never
// reads it; `placeholderData` / `keepPreviousData` would be exactly the bug (A's chart
// under B's name), so neither is set.
//
// Both are idle for `null`: a machine from another org (G23) is never read here, since
// the stats read 404s cross-org and the task list is account-scoped (an empty page that
// would read as "no work").
//
// LIVE (`createLiveQuery`, #7612): the history re-reads when THIS machine moves, the
// recent work when any task moves (a task joining or leaving the machine is not knowable
// by id). A new stats SAMPLE is not a move: the `machines` version ignores heartbeat and
// stats, so the history keeps its own cadence (`refreshMs`, `FLEET_POLL_MS`) while live.

export const MACHINE_HISTORY_QUERY_KEY = 'machine_history';
export const MACHINE_RECENT_WORK_QUERY_KEY = 'machine_recent_work';

/** Rows in Recent work. */
export const RECENT_WORK_ROWS = 5;

/** One machine's stats history over `range`, re-read when that machine moves so "paused" stays true. */
export function useMachineHistory(
  machineId: () => string | null,
  range: () => RunnerStatsRange,
  token: string | null,
) {
  return createLiveQuery<RunnerStatsHistoryWire>(() => {
    const id = machineId();
    const r = range();
    return {
      queryKey: [MACHINE_HISTORY_QUERY_KEY, id, r, token, activeAccountId()],
      enabled: token !== null && id !== null,
      live: id === null ? [] : [{ entity: 'machine', id }],
      refreshMs: FLEET_POLL_MS,
      // Parsed, not cast: a drifted body is a named error state, never an `undefined`
      // read inside a chart.
      queryFn: () =>
        apiFetch(`/v1/runners/${encodeURIComponent(id ?? '')}/stats?range=${r}`, token, undefined, {
          schema: RunnerStatsHistoryWireSchema,
        }),
    };
  }, token).query;
}

/** The machine's newest {@link RECENT_WORK_ROWS} tasks, server-filtered, newest first. */
export function useMachineRecentWork(machineId: () => string | null, token: string | null) {
  return createLiveQuery<Page<TaskRow>>(() => {
    const id = machineId();
    return {
      queryKey: [MACHINE_RECENT_WORK_QUERY_KEY, id, token, activeAccountId()],
      enabled: token !== null && id !== null,
      live: [{ entity: 'task' }],
      queryFn: () =>
        fetchTasksPage(
          null,
          token,
          `${tasksPath({ kind: 'machine', id: id ?? '' })}&per_page=${RECENT_WORK_ROWS}`,
        ),
    };
  }, token).query;
}
